import React, { useState } from 'react';
import { useLayoutContext } from 'contexts/LayoutContext';
import BankReconciliationWarningBox from '../BankReconciliationWarning';
import { CalendarDay } from 'further-ui/utils';
import api from 'lib/trpcClient';
import TransactionAllocator from 'components/TransactionAllocator';
import PageLoader from 'components/PageLoader';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import CurrencyRate from 'components/CurrencyRate';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { ActionRefLabels } from 'further-ui/labels';
import { Alert, Button, Typography } from '@mui/material';
import { useDisclosure } from 'further-ui/hooks';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  createLpPeriodEndReviewDetailRoute,
  LP_PERIOD_END_REVIEW,
} from 'adminConstants/routes';
import ProcessingPage from 'components/new/ProcessingPage';
import Table from 'further-ui/components/Table';
import { styled } from '@mui/material/styles';
import { Row } from 'components/Layout';
import ColorIndicator from 'further-ui/components/ColorIndicator';
import FullPageDrawer from 'components/new/FullPageDrawer';
import { Column } from 'further-ui/components/Layout';

const ExpandedRowContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderTop: `1px solid ${theme.palette.grey[300]}`,
}));

type Props = {
  onExit: () => void;
};

const ApproveTransactionRates: React.FC<Props> = ({ onExit }) => {
  const { vehicle } = useLayoutContext();
  const { search } = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(search);
  const periodStartParam = searchParams.get('periodStart');
  const periodEndParam = searchParams.get('periodEnd');
  const { periodId } = useParams<{ periodId?: string }>();
  const periodStart = periodStartParam
    ? new CalendarDay(periodStartParam)
    : null;
  const periodEnd = periodEndParam
    ? new CalendarDay(periodEndParam)
    : new CalendarDay(new Date());

  const [overrideRates, setOverrideRates] = useState<
    Record<string, CurrencyRateDetails>
  >({});
  const [isProcessingApproval, setIsProcessingApproval] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const { hasEqualisationInDraftState } = useEqualisations(vehicle?._id ?? '');
  const confirmDialog = useDisclosure();

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const { data: actions, isLoading } =
    api.transactionFx.listActionsForRateApproval.useQuery(
      periodStart && periodEnd
        ? {
            vehicleId: vehicle?._id ?? '',
            periodStart,
            periodEnd,
          }
        : {
            vehicleId: vehicle?._id ?? '',
            date: periodEnd,
          },
      { enabled: !!vehicle },
    );

  const mutationOpts = useHandleTrpcMutation();
  const { mutateAsync: approveFxRates } =
    api.transactionFx.approveFxRates.useMutation(
      mutationOpts({
        successMessage: 'Transaction rates approved successfully.',
      }),
    );

  const handleMutation = useHandleTrpcMutation<string>();
  const confirmChecklistItems =
    api.accountingPeriod.confirmChecklistItems.useMutation(
      handleMutation({ refetchType: 'active' }),
    );

  const handleExit = () => {
    onExit();
  };

  const handleApprove = async () => {
    if (!actions?.length || !vehicle?._id) return;
    setIsProcessingApproval(true);
    try {
      await approveFxRates({
        vehicleId: vehicle._id,
        actions: actions.map((action) => ({
          id: action.id,
          currencyRate: overrideRates[action.id] ?? action.currencyRate ?? null,
        })),
      });
      if (periodId) {
        await confirmChecklistItems.mutateAsync({
          vehicleId: vehicle._id,
          id: periodId,
          items: ['transactionRates'],
          confirmed: true,
        });
        history.push(createLpPeriodEndReviewDetailRoute(periodId));
      } else {
        history.push(LP_PERIOD_END_REVIEW);
      }
    } catch {
      setIsProcessingApproval(false);
    }
  };

  const columns = [
    {
      key: 'action',
      label: '',
      render: (row) => (
        <Column spacing="xs">
          <Row centered spacing="sm">
            <ColorIndicator colorKey={{ actionRef: row.actionRef }} />
            <Typography variant="subtitle2">
              {ActionRefLabels[row.actionRef]} – {row.counterpartyLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {row.ledgerDate.toReadable()}
            </Typography>
          </Row>
        </Column>
      ),
      disableSorting: true,
    },
  ];

  if (!vehicle || transactionTypesLoading) {
    return <PageLoader />;
  }

  return (
    <FullPageDrawer
      open
      size="lg"
      onClose={handleExit}
      innerScroll
      title="Automated Transactions: Approve transaction rates"
      actions={
        <Row fullWidth justify="space-between">
          <Button size="large" variant="outlined" onClick={handleExit}>
            Exit
          </Button>
          {!!actions?.length && (
            <Button
              size="large"
              variant="contained"
              onClick={
                hasEqualisationInDraftState
                  ? confirmDialog.onOpen
                  : handleApprove
              }
              disabled={isProcessingApproval}
            >
              Approve and mark as reviewed
            </Button>
          )}
        </Row>
      }
    >
      <Column spacing="md" fullWidth>
        {isProcessingApproval ? (
          <ProcessingPage
            title="Approving transaction rates"
            subtitle="This will only take a moment."
          />
        ) : (
          <>
            <Typography variant="h5">Review transactions</Typography>
            <Typography variant="body2">
              Review and approve outstanding transaction rates from{' '}
              {periodStart?.toReadable()} to {periodEnd?.toReadable()}.
            </Typography>
            <BankReconciliationWarningBox
              date={periodEnd.toUTCDate()}
              warningText="There are pending transactions awaiting bank reconciliation. Click below to process these before reviewing accruals, as reconciled transactions may change outstanding accruals."
            />
            {!isLoading && !actions?.length ? (
              <Alert severity="info-light">
                No transactions found for selected date.
              </Alert>
            ) : (
              <Table
                columns={columns}
                rows={actions ?? []}
                isLoading={isLoading}
                rowsTotalCount={actions?.length ?? 0}
                hideHeader
                isExpandableRow={() => true}
                isExpandedRow={(row) => expandedRowId === row.id}
                onRowExpand={(row) =>
                  setExpandedRowId((prev) => (prev === row.id ? null : row.id))
                }
                getExpandedContent={(row) => (
                  <ExpandedRowContainer>
                    <Row fullWidth>
                      <TransactionAllocator
                        transactions={row.transactions}
                        transactionCurrency={row.currencyCode}
                        vehicle={vehicle}
                        fxRate={
                          overrideRates[row.id] ?? row.currencyRate ?? null
                        }
                        transactionTypeOptions={transactionTypeOptions}
                        readOnly
                        forceLcyCalculation
                        darkBackground
                        currencyRateComponent={
                          <CurrencyRate
                            currency={row.currencyCode}
                            date={row.ledgerDate}
                            vehicle={vehicle}
                            fxRate={
                              overrideRates[row.id] ?? row.currencyRate ?? null
                            }
                            isFetchingRate={false}
                            onRateChange={(rate) => {
                              setOverrideRates((prev) => ({
                                ...prev,
                                [row.id]: rate,
                              }));
                            }}
                          />
                        }
                      />
                    </Row>
                  </ExpandedRowContainer>
                )}
              />
            )}
          </>
        )}
      </Column>
      {hasEqualisationInDraftState && (
        <PendingDraftEqualisationsModal
          isOpen={confirmDialog.isOpen}
          onClose={confirmDialog.onClose}
          onConfirm={handleApprove}
          loading={isProcessingApproval}
        />
      )}
    </FullPageDrawer>
  );
};

export default ApproveTransactionRates;
